/* eslint-disable max-len */
import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { notificationZIndex } from '../../styles/global';

interface UpdateNotificationProps {
  registration: ServiceWorkerRegistration;
}

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid ${(p) => p.theme.colors.primaryDark};
  border-radius: 12px;
  padding: 16px 24px;
  z-index: ${notificationZIndex};
  display: flex;
  align-items: center;
  gap: 16px;
  animation: ${slideIn} 0.4s ease-out;
  max-width: 90vw;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    bottom: 10px;
    left: 10px;
    right: 10px;
    transform: none;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: ${(p) => p.theme.colors.primaryDark};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RefreshIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
      fill="white"
    />
  </svg>
);

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
`;

const Description = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const Button = styled.button<{ $primary?: boolean; }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ theme, $primary }) =>
    $primary
      ? css`
          background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
          color: ${theme.colors.onPrimary};

          &:hover {
            filter: brightness(1.1);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px ${theme.colors.primary}66;
          }
        `
      : css`
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);

          &:hover {
            background: rgba(255, 255, 255, 0.15);
          }
        `}

  &:active {
    transform: translateY(0);
  }
`;

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({ registration }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(true);

  const handleUpdate = useCallback(() => {
    if (registration.waiting) {
      // Tell the service worker to skip waiting and become active
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    // Reload the page to get the new version
    window.location.reload();
  }, [registration]);

  const handleDismiss = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    // Listen for the controlling service worker changing
    // and reload the page
    let refreshing = false;
    const handleControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  if (!show) return null;

  return (
    <Container>
      <IconWrapper>
        <RefreshIcon />
      </IconWrapper>
      <TextContent>
        <Title>{t('newVersionAvailable')}</Title>
        <Description>{t('updateDescription')}</Description>
      </TextContent>
      <ButtonGroup>
        <Button onClick={handleDismiss}>{t('later')}</Button>
        <Button $primary onClick={handleUpdate}>
          {t('updateNow')}
        </Button>
      </ButtonGroup>
    </Container>
  );
};

export default UpdateNotification;
